import React, { memo } from "react";
import styled from "styled-components";
import {
  Accordion,
  AccordionGroup,
  Box,
  Card,
  Heading,
  List,
  ListItem,
  Stack,
  Text,
} from "@origin-digital/ods-core";
import { IconCheck } from "@origin-digital/ods-icons";
import { DialogTrigger, UnderlinedText } from "@/components";
import { IISystemSizeMinMaxResponse } from "@/contract/generated/electrify-v1";
import { formatLabel, formatNumberWithCommas } from "@/utils";
import { solarOutlook as content } from "../content.json";

const ChildList = styled.ul`
  list-style-type: none;
  padding-left: 20px;
`;

const ChildListItem = styled.li`
  position: relative;
  padding-left: 10px;

  &::before {
    content: "–";
    position: absolute;
    left: 0;
  }
`;

type EntireCostProps = {
  recommendationSystem: IISystemSizeMinMaxResponse;
};

export const EntireCost = memo(({ recommendationSystem }: EntireCostProps) => {
  const { label, testId } = content;

  const { min, max } = recommendationSystem;

  return (
    <Box
      data-id={testId.entireCost}
      display="flex"
      marginX={{ xs: "none", lg: "medium" }}
      marginY={{ xs: "small", lg: "none" }}
      width="full"
    >
      <Card outline>
        <Stack space={["medium", "large"]}>
          <Heading variant="h4" weight="bold">
            {label.costOfSystem.heading}
          </Heading>
          <Stack space="large">
            <Stack space="xsmall">
              <Heading variant="h2" weight="bold">
                {`$${formatNumberWithCommas(
                  min?.totalCostOfSystem ?? 0
                )} - $${formatNumberWithCommas(max?.totalCostOfSystem ?? 0)}`}
              </Heading>
              <Text>
                {formatLabel(
                  label.costOfSystem.timeToPayItself,
                  (value) => (
                    <b>{value}</b>
                  ),
                  `${min?.paybackPeriod ?? 0} - ${
                    max?.paybackPeriod ?? 0
                  } years`
                )}
              </Text>
            </Stack>

            <Stack space="xsmall">
              <AccordionGroup>
                <Accordion id="" size="small" summary="What is included">
                  <List>
                    <ListItem icon={<IconCheck />}>
                      {label.costOfSystem.qualityInverter}
                    </ListItem>
                    <ListItem icon={<IconCheck />}>
                      {label.costOfSystem.efficientSolarPanels}
                    </ListItem>
                    <ListItem icon={<IconCheck />}>
                      {label.costOfSystem.professionalInstallation}
                    </ListItem>
                  </List>
                </Accordion>
              </AccordionGroup>
              <Text variant="caption" tone="neutralLight">
                {content.label.costOfSystem.whatIsIncludeDesc}{" "}
                <DialogTrigger
                  id=""
                  title={<>{content.label.costOfSystem.detailedLink}</>}
                  summary={
                    <>
                      <List>
                        {content.label.detailedInclusion.map((item, index) => (
                          <ListItem key={index}>
                            {item.mainText}
                            {item.children && (
                              <ChildList>
                                {item.children.map((child, childIndex) => (
                                  <ChildListItem key={childIndex}>
                                    <Text>{child}</Text>
                                  </ChildListItem>
                                ))}
                              </ChildList>
                            )}
                          </ListItem>
                        ))}
                      </List>

                      <Box marginTop="large">
                        <Text variant="subtitle">
                          {content.label.exclusions.heading}
                        </Text>
                      </Box>

                      <ChildList>
                        {content.label.detailedExclusion.map((item, index) => (
                          <ChildListItem key={index}>
                            <Text>{item.mainText}</Text>
                            {item.children && (
                              <ChildList>
                                {item.children.map((child, childIndex) => (
                                  <ChildListItem key={childIndex}>
                                    <Text>{child}</Text>
                                  </ChildListItem>
                                ))}
                              </ChildList>
                            )}
                          </ChildListItem>
                        ))}
                      </ChildList>
                    </>
                  }
                >
                  <UnderlinedText>
                    {content.label.costOfSystem.detailedLink}
                  </UnderlinedText>
                </DialogTrigger>
              </Text>
            </Stack>
          </Stack>
        </Stack>
      </Card>
    </Box>
  );
});
